import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const openLeadForm = () => {
    window.dispatchEvent(new CustomEvent('openLeadForm'));
  };

  return (
    <header className={`sticky top-0 z-50 bg-white border-b border-slate-100 sticky-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_d9eb1bfb-a60c-4d75-9f49-96d39f58fd2c/artifacts/l736s5r0_Final.png" 
              alt="Tiller Kingsley Bale Associates" 
              className="h-14 md:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center justify-center flex-1 space-x-8">
            <Link to="/about" className="text-slate-700 hover:text-slate-900 font-medium transition-colors" data-testid="nav-about">
              About Us
            </Link>
            
            <Link to="/attorneys" className="text-slate-700 hover:text-slate-900 font-medium transition-colors" data-testid="nav-attorneys">
              Our Attorneys
            </Link>

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button 
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center text-slate-700 hover:text-slate-900 font-medium transition-colors"
                data-testid="nav-services"
              >
                Services <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              
              {servicesOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[380px] bg-white border border-slate-200 shadow-xl py-2 z-50 overflow-visible">
                  <Link to="/services/corporate-business-contracts" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Corporate & Business Contracts
                  </Link>
                  <Link to="/services/corporate-business-formation" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Corporate & Business Formation
                  </Link>
                  <Link to="/services/business-transactions" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Business Transactions
                  </Link>
                  <Link to="/services/mergers-acquisitions" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Mergers & Acquisitions
                  </Link>
                  <Link to="/services/non-profit-law" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Non-Profit Law
                  </Link>
                  <Link to="/services/government-contracting" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Government Contracting
                  </Link>
                  <Link to="/services/business-disputes" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Business Disputes
                  </Link>
                  <Link to="/services/anti-corruption-compliance" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Anti-Corruption Compliance
                  </Link>
                  <Link to="/services/immigration" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Immigration
                  </Link>
                  <Link to="/services/trademark-registration" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Trademark Registration
                  </Link>
                  <Link to="/services/copyright-registration" className="block px-6 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">
                    Copyright Registration
                  </Link>
                </div>
              )}
            </div>

            <Link 
              to="/contact"
              className="text-slate-700 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-contact"
            >
              Contact Us
            </Link>
          </nav>

          {/* CTA Button */}
          <button 
            onClick={openLeadForm}
            className="hidden lg:block bg-slate-900 text-white hover:bg-slate-800 px-6 py-2.5 font-serif text-base transition-all hover:tracking-wider shadow-md hover:shadow-lg"
            data-testid="header-cta-button"
          >
            Get Legal Help
          </button>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-slate-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden border-t border-slate-100"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-4">
                <Link to="/about" className="block text-slate-700 hover:text-slate-900 font-medium py-2">
                  About Us
                </Link>
                
                <Link to="/attorneys" className="block text-slate-700 hover:text-slate-900 font-medium py-2">
                  Our Attorneys
                </Link>

                {/* Services Dropdown for Mobile */}
                <div>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className="flex items-center justify-between w-full text-slate-700 hover:text-slate-900 font-medium py-2"
                  >
                    <span>Services</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div className="pl-4 space-y-2 mt-2">
                      <Link to="/services/corporate-business-contracts" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Corporate & Business Contracts
                      </Link>
                      <Link to="/services/corporate-business-formation" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Corporate & Business Formation
                      </Link>
                      <Link to="/services/business-transactions" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Business Transactions
                      </Link>
                      <Link to="/services/mergers-acquisitions" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Mergers & Acquisitions
                      </Link>
                      <Link to="/services/non-profit-law" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Non-Profit Law
                      </Link>
                      <Link to="/services/government-contracting" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Government Contracting
                      </Link>
                      <Link to="/services/business-disputes" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Business Disputes
                      </Link>
                      <Link to="/services/anti-corruption-compliance" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Anti-Corruption Compliance
                      </Link>
                      <Link to="/services/immigration" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Immigration
                      </Link>
                      <Link to="/services/trademark-registration" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Trademark Registration
                      </Link>
                      <Link to="/services/copyright-registration" className="block text-slate-600 hover:text-slate-900 text-sm py-1">
                        Copyright Registration
                      </Link>
                    </div>
                  )}
                </div>

                <Link 
                  to="/contact"
                  className="block text-slate-700 hover:text-slate-900 font-medium py-2"
                >
                  Contact Us
                </Link>

                <button 
                  onClick={openLeadForm}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 px-6 py-3 font-serif text-base transition-all shadow-md mt-4"
                  data-testid="mobile-cta-button"
                >
                  Get Legal Help
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
